import { BROWSER_GTAG_BLOCKED, SCHEMA_VERSION } from "../events";
import { buildGa4Params } from "../ga4";
import {
  getAnalyticsDebugAtStartup,
  getBrowserMeasurementId,
  getMeasurementId,
  MEASUREMENT_STAGING,
} from "../context";

const GA_ID_TIMEOUT_MS = 1500;
const QUEUE_CAP = 20;

let pendingQueue = [];
let initialized = false;

function isBlockedName(ga4Name, allowPageView) {
  if (!ga4Name) {
    return true;
  }
  if (ga4Name === "page_view") {
    return !allowPageView;
  }
  return ga4Name === "purchase" || ga4Name === "refund" || ga4Name === "conversion";
}

function isAdsDestination(params) {
  const sendTo = params && params.send_to;
  return typeof sendTo === "string" && sendTo.startsWith("AW-");
}

function dispatch(ga4Name, params) {
  const measurementId = getBrowserMeasurementId();
  if (!measurementId || typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }
  if (isAdsDestination(params)) {
    return;
  }
  const payload = {
    ...params,
    send_to: measurementId,
  };
  if (measurementId === MEASUREMENT_STAGING && getAnalyticsDebugAtStartup()) {
    payload.debug_mode = true;
  }
  try {
    window.gtag("event", ga4Name, payload);
  } catch {
    // never block checkout
  }
}

function enqueueOrSend(ga4Name, params, allowPageView) {
  if (isBlockedName(ga4Name, allowPageView) || isAdsDestination(params)) {
    return;
  }
  if (!getBrowserMeasurementId() || typeof window === "undefined") {
    return;
  }
  if (initialized) {
    dispatch(ga4Name, params);
    return;
  }
  if (pendingQueue.length >= QUEUE_CAP) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[analytics] dropped event (queue full):", ga4Name);
    }
    return;
  }
  pendingQueue.push({ ga4Name, params });
}

export function initGa4() {
  if (initialized) {
    return;
  }
  const measurementId = getBrowserMeasurementId();
  if (!measurementId || typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }
  try {
    const configOptions = { send_page_view: false };
    if (measurementId === MEASUREMENT_STAGING && getAnalyticsDebugAtStartup()) {
      configOptions.debug_mode = true;
    }
    window.gtag("config", measurementId, configOptions);
  } catch {
    return;
  }
  initialized = true;
  const queued = pendingQueue;
  pendingQueue = [];
  queued.forEach((entry) => dispatch(entry.ga4Name, entry.params));
}

function sendPageView(params) {
  enqueueOrSend("page_view", params || {}, true);
}

export const ga4Adapter = {
  init: initGa4,
  track(eventName, properties, context) {
    if (BROWSER_GTAG_BLOCKED.includes(eventName)) {
      return;
    }
    const { ga4Name, params } = buildGa4Params(eventName, properties, context || {});
    enqueueOrSend(ga4Name, params, false);
  },
  page(info) {
    const path = (info && info.path) || "/";
    const host = typeof window !== "undefined" && window.location ? window.location.hostname : "";
    sendPageView({
      page_path: path,
      ...(host ? { page_location: `https://${host}${path}` } : {}),
      schema_version: SCHEMA_VERSION,
      language: info && info.language,
      page_type: info && info.pageType,
      anonymous_id: info && info.anonymousId,
      source: "web",
    });
  },
};

export function fetchGaClientAndSessionIds() {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (ids) => {
      if (settled) return;
      settled = true;
      resolve(ids || {});
    };
    const measurementId = getMeasurementId();
    if (!measurementId || typeof window === "undefined" || typeof window.gtag !== "function") {
      finish({});
      return;
    }
    const partial = {};
    const timer = setTimeout(() => finish({ ...partial }), GA_ID_TIMEOUT_MS);

    const done = (ids) => {
      clearTimeout(timer);
      finish(ids);
    };

    const readSession = (clientId) => {
      if (!clientId) {
        done({});
        return;
      }
      partial.ga_client_id = clientId;
      try {
        window.gtag("get", measurementId, "session_id", (sessionId) => {
          const out = { ga_client_id: clientId };
          if (sessionId) {
            out.ga_session_id = sessionId;
          }
          done(out);
        });
      } catch {
        done({ ga_client_id: clientId });
      }
    };

    try {
      window.gtag("get", measurementId, "client_id", (clientId) => {
        readSession(clientId);
      });
    } catch {
      done({});
    }
  });
}

export function _resetGtagAdapterForTests() {
  pendingQueue = [];
  initialized = false;
}
